// src/components/TaskTodoTable.js

import React, { useEffect, useState } from 'react'
import {
    Card,
    CardHeader,
    CardContent,
    Table,
    TableHead,
    TableBody,
    TableCell,
    TableRow,
    Chip,
    Button,
    CircularProgress,
    Box,
    Tooltip,
    Container,
    Divider,
    Stack,
    Typography,
} from '@mui/material'
import { OpenInNew, CheckCircleOutline, History } from '@mui/icons-material'
import { api } from '../../../services/Main/Api'
import moment from 'moment'
import {getCondoId} from "../../../services/Main/AuthStorage";

// 🔧 Ajuste o endpoint conforme sua API real
const TASKS_ENDPOINT = '/tasks/todo/'

const PRIORITY_COLORS = {
    urgent: 'error',
    medium: 'warning',
    low: 'info',
}

const formatDate = (date) => {
    if (!date) return '-'
    return moment(date).format('DD/MM/YYYY [às] HH:mm')
}

const TaskCenter = () => {
    const [tasksData, setTasksData] = useState({ results: [] })
    const [loading, setLoading] = useState(true)
    const [resolvingId, setResolvingId] = useState(null)
    const [statusFilter, setStatusFilter] = useState('pending') // 'pending' | 'resolved'

    const normalizeData = (data) => {
        // Se vier paginado (DRF default: {results: [...]})
        if (data && Array.isArray(data.results)) {
            return data
        }

        // Se vier como lista simples [...]
        if (Array.isArray(data)) {
            return { results: data }
        }

        return { results: [] }
    }

    const loadTasks = async (status = statusFilter) => {
        try {
            setLoading(true)
            const response = await api.get(TASKS_ENDPOINT, { params: { status, condo_id: getCondoId() } })
            setTasksData(normalizeData(response.data))
        } catch (e) {
            console.error('Erro ao carregar tarefas', e)
            setTasksData({ results: [] })
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        loadTasks('pending')
    }, [])

    const handleToggleStatus = async () => {
        const nextStatus = statusFilter === 'pending' ? 'resolved' : 'pending'
        setStatusFilter(nextStatus)
        await loadTasks(nextStatus)
    }

    const handleMarkResolved = async (taskId) => {
        try {
            setResolvingId(taskId)
            await api.post(`/tasks/${taskId}/resolve/`)

            // Se estiver vendo pendentes, removemos da lista local
            if (statusFilter === 'pending') {
                setTasksData((prev) => ({
                    ...prev,
                    results: (prev.results || []).filter((t) => t.id !== taskId),
                }))
            } else {
                // Se estiver vendo resolvidas, apenas recarrega
                await loadTasks('resolved')
            }
        } catch (e) {
            console.error('Erro ao marcar tarefa como resolvida', e)
        } finally {
            setResolvingId(null)
        }
    }

    const taskList = tasksData.results || []
    const isResolvedView = statusFilter === 'resolved'

    return (
        <Container maxWidth={false}>
            <Card>
                <CardHeader
                    title="Alertas"
                    action={
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<History />}
                            onClick={handleToggleStatus}
                        >
                            {isResolvedView ? 'Ver pendentes' : 'Ver resolvidas'}
                        </Button>
                    }
                />
                <Divider />
                <CardContent>
                    {loading ? (
                        <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Table>
                            <TableHead>
                                <TableRow>
                                    {/*<TableCell>Prioridade</TableCell>*/}
                                    <TableCell>Tarefa</TableCell>
                                    <TableCell>Descrição</TableCell>
                                    <TableCell>
                                        {isResolvedView ? 'Resolvida em' : 'Criada em'}
                                    </TableCell>
                                    <TableCell align="right">
                                        {isResolvedView ? 'Ações' : 'Ação'}
                                    </TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {taskList.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center">
                                            {isResolvedView
                                                ? 'Nenhuma tarefa resolvida ainda.'
                                                : 'Nenhuma tarefa pendente 🎉'}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    taskList.map((task) => {
                                        const priorityLabel =
                                            task.priority === 'urgent'
                                                ? 'Urgente'
                                                : task.priority === 'medium'
                                                    ? 'Média'
                                                    : 'Baixa'

                                        const dateToShow = isResolvedView
                                            ? task.is_done || task.updated_at || task.created_at
                                            : task.created_at

                                        return (
                                            <TableRow key={task.id}>
                                                {/*<TableCell>*/}
                                                {/*    <Chip*/}
                                                {/*        label={priorityLabel}*/}
                                                {/*        color={PRIORITY_COLORS[task.priority] || 'default'}*/}
                                                {/*        variant="outlined"*/}
                                                {/*        size="small"*/}
                                                {/*    />*/}
                                                {/*</TableCell>*/}

                                                <TableCell>
                                                    <strong>{task.title}</strong>
                                                </TableCell>

                                                <TableCell>{task.description}</TableCell>

                                                <TableCell>{formatDate(dateToShow)}</TableCell>

                                                <TableCell align="right">
                                                    <Stack
                                                        direction="row"
                                                        spacing={1}
                                                        justifyContent="flex-end"
                                                        alignItems="center"
                                                    >
                                                        {task.action_url && (
                                                            <Tooltip title="Ir para a tela relacionada">
                                <span>
                                  <Button
                                      size="small"
                                      variant="outlined"
                                      endIcon={<OpenInNew />}
                                      onClick={() =>
                                          window.open(task.action_url, '_blank')
                                      }
                                  >
                                    Abrir
                                  </Button>
                                </span>
                                                            </Tooltip>
                                                        )}

                                                        {!isResolvedView && (
                                                            <Tooltip title="Marcar tarefa como resolvida">
                                <span>
                                  <Button
                                      size="small"
                                      variant="outlined"
                                      color="success"
                                      endIcon={<CheckCircleOutline />}
                                      disabled={resolvingId === task.id}
                                      onClick={() => handleMarkResolved(task.id)}
                                  >
                                    {resolvingId === task.id
                                        ? 'Salvando...'
                                        : 'Marcar como resolvida'}
                                  </Button>
                                </span>
                                                            </Tooltip>
                                                        )}

                                                        {isResolvedView && (
                                                            <Chip
                                                                label="Resolvida"
                                                                size="small"
                                                                color="success"
                                                                variant="outlined"
                                                            />
                                                        )}
                                                    </Stack>

                                                    {/* Opcional: info de quem resolveu, se o serializer mandar is_done_by_user */}
                                                    {isResolvedView && task.is_done_by_user && (
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            sx={{ display: 'block', mt: 0.5 }}
                                                        >
                                                            Resolvida por usuário #{task.is_done_by_user}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })
                                )}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>
        </Container>
    )
}

export default TaskCenter
